import * as types from 'types';
import { IImportOffer } from 'types';
import {
  IPersonalInfo,
  IOffer,
  IHistory,
  MaritalStatus,
  IImportCourse,
  ICourse,
  IImportData,
  IAgent,
  IConflictHistory,
  IStudentConfig
} from '../types/index.d';
import { getImportOffer } from './helpers';

export function hasSpouseDetails(maritalStatus: MaritalStatus) {
  return (
    maritalStatus === MaritalStatus.MARRIED ||
    maritalStatus === MaritalStatus.UNMARRIED ||
    maritalStatus === MaritalStatus.SEPARATED
  );
}

export function isPersonTravellingWith(travellingWith?: string) {
  return travellingWith === 'Yes - under 1 month' || travellingWith === 'Yes - over 1 month';
}

export function visaPrepFlags(
  personalInfo: IPersonalInfo,
  history: IHistory,
  offer: IOffer
): {
  nonHomeCountryApplication: boolean;
  hasTravelledToUk: boolean;
  hasLeaveToRemain: boolean;
  hasBorderRefusal: boolean;
  hasOtherTravellers: boolean;
  hasTakenEnglishTest: boolean;
} {
  return {
    nonHomeCountryApplication: personalInfo.applyingFromHomeCountry === false,
    hasTravelledToUk: history.hasOverseasTravel === true && history.hasUkTravel === true,
    hasLeaveToRemain: history.hasLeaveToRemain === true,
    hasBorderRefusal: history.hasBorderRefusal === true,
    hasOtherTravellers: offer.hasDependents === true,
    hasTakenEnglishTest: personalInfo.hasEnglishTest === true
  };
  // flags hide/show visa pages, uncomment below to show all visa pages
  // return {
  //   nonHomeCountryApplication: true,
  //   hasTravelledToUk: true,
  //   hasLeaveToRemain: true,
  //   hasBorderRefusal: true,
  //   hasOtherTravellers: true,
  //   hasTakenEnglishTest: true
  // };
}

export function compareCourses(importCourse: IImportCourse, course: ICourse): boolean {
  if (!importCourse || !course) {
    return false;
  }

  const courseNameConflict = !importCourse.title || (course.courseName ?? '') !== importCourse.title;
  const startDateConflict = !importCourse.startDate || (course.startDate ?? '') !== importCourse.startDate;
  const endDateConflict = !importCourse.endDate || (course.endDate ?? '') !== importCourse.endDate;
  const courseFeeConflict =
    importCourse.courseFeeFirstYear === undefined || (course.courseFee ?? 0) !== importCourse.courseFeeFirstYear;
  const isWorkPlacementConflict =
    importCourse.isWorkPlacement === undefined || (course.isWorkPlacement ?? false) !== importCourse.isWorkPlacement;

  if (courseNameConflict || startDateConflict || endDateConflict || courseFeeConflict || isWorkPlacementConflict) {
    return false;
  } else {
    return true;
  }
}

/* personalInfo.withAgent === 'Agent' - student answered they have an agent.
 * personalInfo.withAgent !== 'Agent' - student has not answered the question, or answered they do not have an agent.
 */
export function isStudentAnswerWithAgent(personalInfo: IPersonalInfo) {
  return personalInfo?.withAgent === 'Agent';
}

/* importData.withAgent === true - student confirmed by uni to have an agent.
 * importData.withAgent === false - student confirmed by uni to not have an agent.
 * importData.withAgent === undefined - uni has not data about whether student has an agent or not.
 */
export function isStudentImportWithAgent(importOffer: IImportOffer | null) {
  return importOffer?.withAgent === true;
}

/* Student 'with Agent' status may be assigned in Imported Data
 * or when student clarifies it in the Personal Info question
 * or when student is actually assigned to an outside agent.
 */
export function isStudentWithAgent(
  personalInfo: IPersonalInfo,
  importOffer: IImportOffer | null,
  offer: IOffer,
  includeAnswer = false
) {
  const agentList = getAgentList(offer?.assignedAgents, importOffer?.agentName);
  return (
    (includeAnswer && isStudentAnswerWithAgent(personalInfo)) ||
    isStudentImportWithAgent(importOffer) ||
    isAssignedToOutsideAgent(agentList)
  );
}

export function isAssignedToOutsideAgent(agentList?: Array<string>) {
  return (
    agentList !== undefined &&
    agentList?.length > 0 &&
    agentList?.filter((agentName: string) => agentName === 'Enroly').length === 0
  );
}

/* canShow* function logic is used in multiple repositories */
export function canShowAgentQuestion(importData: IImportData, offer: IOffer) {
  const importOffer = getImportOffer(importData, offer.intakeId, offer.offerId);
  const agentList = getAgentList(offer?.assignedAgents, importOffer?.agentName);
  return !isStudentImportWithAgent(importOffer) && !isAssignedToOutsideAgent(agentList);
}

/* canShow* function logic is used in multiple repositories */
export function canShowVisaSupportQuestion(
  personalInfo: IPersonalInfo,
  importData: IImportData,
  config: IStudentConfig,
  offer: IOffer
) {
  const importOffer = getImportOffer(importData, offer.intakeId, offer.offerId);
  const visaSupportOption = config?.otherOptions?.visaSupport;
  const isActive = visaSupportOption === types.OptionStatusType.ACTIVE && !offer?.visaSupportLocked;

  return !isStudentWithAgent(personalInfo, importOffer, offer, true) && isActive;
}

/* canShow* function logic is used in multiple repositories */
export function canShowWhatsappQuestion(offer: IOffer) {
  return offer.visaSupport === true;
}

export function getAgentList(assignedAgents?: IAgent[], importDateAgentName?: string): Array<string> | undefined {
  const dbCoreAgentList = assignedAgents?.length
    ? assignedAgents.map(agent => agent?.id ?? '').filter(agent => agent !== '')
    : [];

  if (dbCoreAgentList.length) {
    return dbCoreAgentList;
  }
  if (importDateAgentName?.length) {
    return [importDateAgentName];
  }

  return undefined;
}

/* canShow* function logic is used in multiple repositories */
export function canShowOfferEvidenceQuestion(
  offer: IOffer,
  importData: IImportData,
  conflictHistory: IConflictHistory,
  config: types.IConfigDefault
) {
  // The page shows in the student journey only if there is evidence used for offer text
  // that has been imported or manually added by the university/pathway provider.
  // If no text is added/imported do not show this page to the student.

  const configFlag = !!config.evidenceForOfferInCourseDetails;

  const importOffer = getImportOffer(importData, offer?.intakeId, offer?.offerId);
  const courses = importOffer?.courses ?? [];
  const coursesHaveOfferEvidence = courses.some((course, i) => {
    const piCourse: ICourse | null = offer?.courses?.[i] ?? null;
    const isCourseRemoved = piCourse !== null && !piCourse.courseName;
    if (isCourseRemoved) {
      return false;
    }

    const conflict = conflictHistory?.courseOfferEvidence?.find(x => x.index === i)?.data;

    const hasImportValue = !!course?.offerEvidence;
    const hasConflictValue = !!conflict?.resolvedValue;

    return hasImportValue || hasConflictValue;
  });

  return configFlag && coursesHaveOfferEvidence;
}
